import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { OrderMinimumViewModel } from '@order/models/order-minimum-view-model';
import { OrderViewModel } from '@order/models/order-view-model';
import { OrderService } from '@order/services/order.service';
import { OrderInfoSidebarService } from '@order/services/order-info-sidebar.service';
import {
  jwtDecode,
  PanAppState,
  PlatformEnum,
  PlatformService,
} from '@panamax/app-state';
import {
  MyOrdersStoreService,
  OrderHeader,
  OrderItem,
  Tallies,
  UqeProductNumber,
} from '@usf/ngrx-order';
import { filter, map, take, distinctUntilChanged } from 'rxjs/operators';
import { ModalService } from '@shared/services/modal/modal.service';
import { IonInput, ModalController, PopoverController } from '@ionic/angular';
import { PoPopoverComponent } from '@order/components/purchase-order/po-popover-desktop/po-popover.component';
import { Observable, Subscription, of, combineLatest } from 'rxjs';
import { ProductAlertMessage } from '@product-detail/models/product-alert-message';
import { TranslateService } from '@ngx-translate/core';
import { getQOEProductWarningMessage } from '@product-detail/utils/quick-order-entry-product.util';
import { OrderItemService } from '@shared/services/order-item.service';
import { QuantityUpdate } from '@shared/models/quantity-update';
import { ApiUpdatable } from '@shared/models/api-updatable';
import { ProductPropertiesEnum } from '@usf/product-types';
import { OrderTracingService } from '@order/tracking/tracing/order-tracing.service';
import { QuantityInputComponent } from '../quantity-input/quantity-input.component';
import { QuickOrderEntryService } from '@shared/services/quick-order/quick-order-entry.service';
import { QuickOrderItem } from '@order/models/quick-order-entry-view-model';
import { OrderAnalyticsService } from '@order/tracking/analytics/order-analytics.service';
import { SearchService } from '@search/services/search.service';
import { QuantityType } from '@shared/models/quantity-type';
import { SimpleActionModalComponent } from '../simple-action-modal/simple-action-modal.component';
import { PATHS } from '@shared/constants/paths';
import { isProntoDay } from '@shared/helpers/pronto.helpers';

@Component({
  selector: 'app-order-info-sidebar',
  templateUrl: './order-info-sidebar.component.html',
  styleUrls: ['./order-info-sidebar.component.scss'],
})
export class OrderInfoSidebarComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  readonly platformEnums = PlatformEnum;
  isCustomerRequirePO = false;
  isPoDisabledForPunchOut = false;
  enableSubmitButton$: Observable<boolean> = of(false);
  totalPrice$: Observable<number>;
  orderViewModel$: Observable<OrderViewModel> =
    this.orderService.loadOrderViewModel$();
  orderWeight$: Observable<number>;
  @Input() platform: PlatformEnum;
  @Input() isList: boolean;
  @Input() isCart: boolean;
  @Input() shippingSavings: number;
  @Input() submitButtonText: string;
  @Input() showPrintIcon = true;
  @Input() disableSubmit: boolean;
  @Input() isShipmentTypeVendor: boolean;

  @Output() submitOrder: EventEmitter<void> = new EventEmitter();
  @Output() changeDeliveryDate: EventEmitter<void> = new EventEmitter();
  @Output() cancelOrder: EventEmitter<void> = new EventEmitter();
  @Output() printOrder: EventEmitter<void> = new EventEmitter();
  @Output() downloadOrder: EventEmitter<void> = new EventEmitter();
  @Output() shareOrder: EventEmitter<void> = new EventEmitter();

  @Input() set orderItemTallies(newTallies: Tallies) {
    this._orderItemTallies = newTallies;
    this.getMinimumOrderMetDetails();
  }
  @Input() set orderHeader(newOrderHeader: OrderHeader) {
    this._orderHeader = newOrderHeader;
    this.getMinimumOrderMetDetails();
  }
  @Input() set isOnMinimumOrderMetFlag(flag: boolean) {
    this._isOnMinimumOrderMetFlag = flag;
    this.getMinimumOrderMetDetails();
  }
  @Input() set orderMinVm(vm: OrderMinimumViewModel) {
    this.orderMinimumDetails = vm;
  }
  @ViewChild('productSearchField', { static: false })
  productSearchField: IonInput;
  @ViewChild('customerProductNumberSearchField', { static: false })
  customerProductNumberSearchField: IonInput;
  @ViewChild('eachesInput', { static: false })
  eachesInput: QuantityInputComponent;
  @ViewChild('casesInput', { static: false })
  casesInput: QuantityInputComponent;

  desktopPopoverReady = false;
  _orderItemTallies: Tallies;
  _orderHeader: OrderHeader;
  _isOnMinimumOrderMetFlag: boolean;
  orderMinimumDetails: OrderMinimumViewModel;
  vm: OrderViewModel;
  // private flag to prevent multiple subscriptions at once
  waitingForViewModelSub: boolean;
  poRegex = new RegExp('^[-0-9a-zA-Z ]{1,20}$');

  //quick order
  productNumberSearchInput: number;
  customerProductNumberSearchInput = '';
  currentQoeCpn: string;

  productSearchLoadingSpinner = false;
  customerProductSearchLoadingSpinner = false;

  quickOrderItem: QuickOrderItem;
  cpnAssociatedProductNumber: number;
  productAddedSuccessMessage = '';
  qoeQuantityCS: ApiUpdatable<number>;
  qoeQuantityEA: ApiUpdatable<number>;
  routerSub$: Subscription;
  confirmDiscardOrderModalOpen = false;
  origOrderItems: OrderItem[];
  customerNumber: number;
  readonly propertiesEnum = ProductPropertiesEnum;
  hasUpdatedPo = false;
  productsIdsSub$: Subscription;
  prontoDaysSub: Subscription;
  prontoDeliveryDays: number[];
  isProntoDay: boolean;
  orderMinVmSub: Subscription;
  constructor(
    private router: Router,
    readonly orderService: OrderService,
    private orderItemService: OrderItemService,
    private orderInfoSidebarService: OrderInfoSidebarService,
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    private modalService: ModalService,
    public modalController: ModalController,
    private translateService: TranslateService,
    private orderTracingService: OrderTracingService,
    private quickOrderEntryService: QuickOrderEntryService,
    private orderAnalyticsService: OrderAnalyticsService,
    public popoverController: PopoverController,
    readonly searchService: SearchService,
    private cdr: ChangeDetectorRef,
    private myOrderStoreService: MyOrdersStoreService,
  ) {}

  ngOnInit(): void {
    this.hasUpdatedPo = false;
    this.isPORequire();
    this.validatePoForPunchOutUser();
    this.totalPrice$ = this.orderService.orderTotalPrice();
    this.enableSubmitButton$ =
      this.orderInfoSidebarService.orderUserAccessStatus();

    this.routerSub$ = this.router?.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.reinitializeWidget(true);
      });

    this.orderViewModel$ = this.orderService.loadOrderViewModel$();
    this.orderWeight$ = this.orderViewModel$.pipe(
      map(vm => vm.orderTallies?.orderWeight),
    );
    //To prevent unnecessary API calls, the Subscription only triggers updates when products or shipping types change
    this.productsIdsSub$ = combineLatest([
      this.orderViewModel$,
      this.totalPrice$,
    ])
      .pipe(
        filter(([vm, totalPrice]) => vm?.allProductIds?.length > 0),
        map(([vm, totalPrice]) => {
          return {
            hasOrder: vm.hasOrder,
            productIds: vm.allProductIds ?? [],
            shipmentTypes:
              vm.shipments?.map(shipment => shipment.shipmentType) ?? [],
            totalPrice: totalPrice,
          };
        }),
        distinctUntilChanged(
          (prev, curr) =>
            prev.totalPrice === curr.totalPrice &&
            prev.hasOrder === curr.hasOrder &&
            prev.productIds.length === curr.productIds.length &&
            prev.productIds.every(
              (id, index) => id === curr.productIds[index],
            ) &&
            prev.shipmentTypes.length === curr.shipmentTypes.length &&
            prev.shipmentTypes.every(
              (type, index) => type === curr.shipmentTypes[index],
            ),
        ),
      )
      .subscribe(({ hasOrder, productIds, shipmentTypes, totalPrice }) => {
        // load products information for the products in the order
        this.quickOrderEntryService.getProductsForQuickOrder$(productIds);
        // load minimum order met details
        this.getMinimumOrderMetDetails();
      });

    this.fetchOriginalOrderItems();
  }

  ngOnDestroy(): void {
    if (this.routerSub$) {
      this.routerSub$.unsubscribe();
    }
    if (this.productsIdsSub$) {
      this.productsIdsSub$.unsubscribe();
    }
    if (this.prontoDaysSub) {
      this.prontoDaysSub.unsubscribe();
    }
    if (this.orderMinVmSub) {
      this.orderMinVmSub.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['orderHeader']) {
      this.isPORequire();
      this.validatePoForPunchOutUser();
    }
  }

  pasteFired(event: any, isCpnInput: boolean) {
    if (isCpnInput) {
      this.customerProductNumberSearchInput = event?.target?.value?.substring(
        0,
        20,
      );
    } else {
      event.preventDefault();
      const current: string = event?.target?.nativeElement?.value || '';
      const cursorPos = event?.target?.selectionStart;
      const pasteText = event?.clipboardData?.getData('text') as string;
      const numericOnly = pasteText.replace(/[^0-9]/g, '');
      const next: string = (
        current.slice(0, cursorPos) +
        numericOnly +
        current.slice(cursorPos)
      )?.substring(0, 7);

      this.productNumberSearchInput = Number(next);
    }
  }

  routeTo(path: string) {
    this.router.navigate([path]);
  }

  async getMinimumOrderMetDetails() {
    if (
      this._isOnMinimumOrderMetFlag &&
      !!this._orderHeader &&
      !this.waitingForViewModelSub
    ) {
      this.waitingForViewModelSub = true;
      this.orderMinVmSub = this.orderService
        .loadOrderMinimumViewModel$()
        .subscribe((vm: OrderMinimumViewModel) => {
          this.orderMinimumDetails = vm;
          this.waitingForViewModelSub = false;
        });
    }
    this.prontoDaysSub = this.orderService
      .selectedCustomerProntoDeliveryDays()
      .subscribe(selectedDays => {
        this.prontoDeliveryDays = selectedDays;
        //true for pronto  date and false for standard date
        this.isProntoDay = isProntoDay(
          this.prontoDeliveryDays,
          this._orderHeader?.requestedDeliveryDate?.toString(),
        );
      });
  }

  async goToDirect() {
    this.orderAnalyticsService.trackBuyMoreAndSaveClick();
    this.searchService.goToDirect();
    await this.popoverController.dismiss();
  }

  isPORequire(): void {
    this.panAppState.accessToken$
      .pipe(
        filter(token => !!token),
        take(1),
      )
      .subscribe(token => {
        const decoded = jwtDecode(token);
        if (decoded) {
          this.customerNumber = decoded['usf-claims'].customerNumber;
          this.isCustomerRequirePO =
            decoded['usf-claims'].requireCustomerPO === 'Y' ? true : false;
        }
      });
  }

  async presentChangePOPopover() {
    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      PoPopoverComponent,
      {},
      'modal-desktop-size-sm',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    return await modal.present();
  }

  submitOrderClicked(event: any) {
    this.submitOrder.emit();
    event.target.disabled = true;
    setTimeout(() => {
      event.target.disabled = false;
    }, 3000);
  }

  discardOrderPopUpModalOpen = async () => {
    if (!this.confirmDiscardOrderModalOpen) {
      if (this.platformService.isTouch.value) {
        this.modalService.setModalOptions(
          true,
          SimpleActionModalComponent,
          {
            title: this.translateService.instant(
              'i18n.orderPage.confirmDiscardOrderTitle',
            ),
            withIcon: false,
            content: this.translateService.instant(
              'i18n.orderPage.confirmDiscardOrderSubTitle',
            ),
            exitButtonForTablet: true,
            exitButtonForMobile: true,
            leftButtonText: this.translateService.instant('i18n.common.cancel'),
            rightButtonText: this.translateService.instant(
              'i18n.orderPage.discardEdits',
            ),
          },
          'simple-action-modal',
        );
      } else {
        this.modalService.setModalOptions(
          false,
          SimpleActionModalComponent,
          {
            title: this.translateService.instant(
              'i18n.orderPage.confirmDiscardOrderTitle',
            ),
            withIcon: false,
            content: this.translateService.instant(
              'i18n.orderPage.confirmDiscardOrderSubTitle',
            ),
            exitButtonForTablet: true,
            exitButtonForMobile: true,
            leftButtonText: this.translateService.instant('i18n.common.cancel'),
            rightButtonText: this.translateService.instant(
              'i18n.orderPage.discardEdits',
            ),
          },
          'simple-action-modal',
        );
      }

      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );

      modal.onDidDismiss().then((resp: any) => {
        if (resp?.data?.dispatchAction) {
          this.orderService.getDirectShippingSavings(
            {
              orderHeader: this._orderHeader,
              orderItems: this.origOrderItems,
            },
            undefined,
            false,
          );
          this.orderService.resetOrder();
          this.routeTo(PATHS.MY_ORDERS_VIEW);
        }
        this.confirmDiscardOrderModalOpen = false;
      });
      this.confirmDiscardOrderModalOpen = true;
      return await modal.present();
    }
  };

  fetchOriginalOrderItems(): void {
    const fullOrderId = `${this._orderHeader?.customerNumber}:${this._orderHeader?.departmentNumber}:${this._orderHeader?.orderId}`;
    this.myOrderStoreService
      .getOrderWithId(fullOrderId)
      .pipe(take(1))
      .subscribe(order => (this.origOrderItems = order?.orderItems));
  }

  changeDeliveryDateClicked(event: any) {
    this.changeDeliveryDate.emit(event);
  }

  cancelOrderClicked() {
    this.cancelOrder.emit();
  }

  printOrderClicked() {
    this.printOrder.emit();
  }

  shareOrderClicked() {
    this.shareOrder.emit();
  }

  downloadOrderClicked() {
    this.downloadOrder.emit();
  }

  validatePoForPunchOutUser() {
    this.panAppState.accessToken$
      .pipe(
        filter(token => !!token),
        take(1),
      )
      .subscribe(token => {
        const decoded = jwtDecode(token);
        if (decoded) {
          this.isPoDisabledForPunchOut =
            decoded['usf-claims']?.punchoutSession ||
            this._orderHeader?.addOrderSource === 'PO';
        }
      });
  }

  qoeProductSearchHandler(
    cpnProductNumber?: number,
    isQOECustomerProduct?: boolean,
  ) {
    const isCpnFlow = !!cpnProductNumber;
    let productNumber = isCpnFlow
      ? Number(cpnProductNumber)
      : Number(this.productNumberSearchInput);
    if (!isCpnFlow && !productNumber) {
      const numericOnly = this.productNumberSearchInput
        ?.toString()
        .replace(/\D/g, '');
      const result = parseInt(numericOnly, 10);
      productNumber = result;
    }
    if (!productNumber) {
      this.reinitializeWidget(false);
    }

    if (this.shouldPerformProductSearch(productNumber)) {
      isCpnFlow
        ? (this.customerProductSearchLoadingSpinner = true)
        : (this.productSearchLoadingSpinner = true);

      this.qoeProductSearch(productNumber, isQOECustomerProduct);
    }
  }

  qoeCustomerProductSearchHandler() {
    if (!!this.cpnInput) {
      this.qoeCustomerProductSearch(this.cpnInput);
    } else {
      this.reinitializeWidget(true);
    }
  }
  async qoeProductSearch(productNumber: number, isCpnFlow: boolean) {
    try {
      const qoeItem =
        await this.quickOrderEntryService.qoeProductSearch(productNumber);
      this.quickOrderItem = qoeItem;
      this.updateQoeQuantities(
        qoeItem?.orderItemInCart?.unitsOrdered?.currentValue,
        false,
      );
      this.updateQoeQuantities(
        qoeItem?.orderItemInCart?.eachesOrdered?.currentValue,
        true,
      );

      this.orderTracingService.trackQEProductSearch(
        this.quickOrderItem?.product,
        this.getProductWarningMessage()?.primaryText ||
          this.getProductWarningMessage()?.secondaryText,
        isCpnFlow,
      );

      this.productSearchLoadingSpinner = false;
      this.customerProductSearchLoadingSpinner = false;
    } catch (error) {
      this.quickOrderItem = {
        productNumber,
        loadingError: true,
        product: null,
      };
    }
  }

  async qoeCustomerProductSearch(customerProductNumber: string) {
    this.currentQoeCpn = customerProductNumber;
    const productNumber =
      await this.quickOrderEntryService.qoeCustomerProductNumberSearch(
        customerProductNumber,
      );
    this.cpnAssociatedProductNumber = productNumber;
    if (productNumber) {
      this.qoeProductSearchHandler(productNumber, true);
    } else {
      this.quickOrderItem = null;
    }
  }

  changeQuantityHandler($event: QuantityUpdate) {
    this.updateQoeQuantities($event?.quantity, $event.isEaches);

    this.addQoeProductToCart(
      $event.productNumber,
      $event.quantity,
      $event.isEaches,
      !!this.customerProductNumberSearchInput,
    );
  }

  addQoeProductToCart(
    productNumber: number,
    quantity: number,
    isEaches?: boolean,
    cpnFlow?: boolean,
  ) {
    this.orderItemService.changeQuantity(
      productNumber,
      quantity,
      isEaches,
      null,
      true,
      !!this.router?.url?.endsWith('/order'), //only make DSS call from cart page
    );
    if (!!this.quickOrderItem) {
      if (isEaches && quantity >= 0) {
        this.quickOrderItem.eachesOrdered = {
          currentValue: quantity,
          previousValue: this.qoeQuantityEA?.previousValue,
          loading: false,
          error: null,
        };
      }
      if (!isEaches && quantity >= 0) {
        this.quickOrderItem.casesOrdered = {
          currentValue: quantity,
          previousValue: this.qoeQuantityCS?.previousValue,
          loading: false,
          error: null,
        };
      }

      this.productAddedSuccessMessage = this.getCartUpdatedSuccessMessage(
        isEaches
          ? !!this.qoeQuantityEA?.previousValue
          : !!this.qoeQuantityCS?.previousValue,
      );

      const uqeProductNumber: UqeProductNumber = {
        productNumber,
        pimGroupId: Number(this.quickOrderItem?.product?.summary?.categoryCode),
      };
      this.orderAnalyticsService.trackQOEAddToOrder(
        this._orderHeader,
        this.quickOrderItem,
        isEaches ? QuantityType.Eaches : QuantityType.Cases,
      );
      this.orderService.loadUnusualQuantities([uqeProductNumber]);
    }

    setTimeout(() => {
      this.productAddedSuccessMessage = '';
    }, 5000);
  }

  updateQoeQuantities(quantity: number, isEaches: boolean) {
    if (isEaches) {
      const previousValue = this.qoeQuantityEA?.currentValue;
      this.qoeQuantityEA = {
        currentValue: quantity,
        previousValue,
        loading: false,
        error: null,
      };
    } else {
      const previousValue = this.qoeQuantityCS?.currentValue;
      this.qoeQuantityCS = {
        currentValue: quantity,
        previousValue,
        loading: false,
        error: null,
      };
    }
  }

  reinitializeWidget(clearSearchInput: boolean) {
    if (clearSearchInput) {
      this.productNumberSearchInput = null;
      this.customerProductNumberSearchInput = '';
    }
    this.currentQoeCpn = null;
    this.quickOrderItem = null;
    this.qoeQuantityCS = null;
    this.qoeQuantityEA = null;
  }

  shouldPerformProductSearch(productNumber: number): boolean {
    return (
      !!productNumber && productNumber !== this.quickOrderItem?.productNumber
    );
  }

  handleQuantityKeydown($event, isEaches: boolean) {
    if (
      !this.quickOrderItem?.product?.summary?.breakable ||
      (this.quickOrderItem?.product?.summary?.breakable && isEaches)
    ) {
      $event.preventDefault();
      this.highlightProductNumber(!!this.customerProductNumberSearchInput);
    } else if (this.quickOrderItem?.product?.summary?.breakable && !isEaches) {
      if (this.eachesInput) {
        setTimeout(() => {
          this.eachesInput?.itemInputBox?.setFocus();
        }, 0);
      }
    }
  }

  highlightProductNumber(isCpnFlow: boolean) {
    const searchField = isCpnFlow
      ? this.customerProductNumberSearchField
      : this.productSearchField;

    // using setTimeout to ensure that the focus and highlight happen after the view is updated
    if (searchField) {
      setTimeout(() => {
        searchField.getInputElement().then(inputElement => {
          searchField.setFocus();
          inputElement.setSelectionRange(0, inputElement.value.length);
        });
      }, 0);
    }
  }

  getCartUpdatedSuccessMessage(isQuantityAdjustment: boolean): string {
    return isQuantityAdjustment
      ? `${this.translateService.instant(
          'i18n.orderInfoSidebar.quickOrderWidget.quantityUpdated',
        )}`
      : `${this.translateService.instant(
          'i18n.orderInfoSidebar.quickOrderWidget.addedToCart',
        )}`;
  }

  handleTabKey(event, isCpnSearch: boolean) {
    const newProductSearch = isCpnSearch
      ? this.newCustomerProductNumberSearch
      : this.newProductNumberSearch;
    if (
      ((isCpnSearch ? this.cpnInput !== '' : !!this.isProductSearch) &&
        !this.showQuantityInputs) ||
      newProductSearch
    ) {
      event.preventDefault();
      isCpnSearch
        ? this.qoeCustomerProductSearchHandler()
        : this.qoeProductSearchHandler();
    } else if (isCpnSearch && !this.cpnInput) {
      event.preventDefault();
      this.productSearchField?.setFocus();
    } else if (!isCpnSearch && !this.isProductSearch) {
      event.preventDefault();
      this.customerProductNumberSearchField?.setFocus();
    } else {
      event.preventDefault();
      this.casesInput?.itemInputBox?.setFocus();
    }
  }

  handleEnterKey(event, isCpnSearch: boolean) {
    const newProductSearch = isCpnSearch
      ? this.newCustomerProductNumberSearch
      : this.newProductNumberSearch;
    if (
      (isCpnSearch ? this.cpnInput !== '' : !!this.isProductSearch) &&
      (!this.showQuantityInputs || newProductSearch)
    ) {
      isCpnSearch
        ? this.qoeCustomerProductSearchHandler()
        : this.qoeProductSearchHandler();
    } else if (isCpnSearch && this.cpnInput === '') {
      event.preventDefault();
      this.productSearchField?.setFocus();
    } else if (!isCpnSearch && !this.isProductSearch) {
      event.preventDefault();
      this.customerProductNumberSearchField?.setFocus();
    } else {
      this.casesInput?.itemInputBox?.setFocus();
    }
  }

  ngAfterViewInit() {
    this.desktopPopoverReady = !!document.getElementById('hover-trigger');
    this.cdr.detectChanges();
  }

  getProductWarningMessage(): ProductAlertMessage {
    return getQOEProductWarningMessage(
      this.translateService,
      this.quickOrderItem?.product,
      this.cpnNotFound,
    );
  }

  get touchPopoverReady(): boolean {
    return !!document.getElementById('click-trigger');
  }

  get cpnNotFound(): boolean {
    return !!this.currentQoeCpn && !this.cpnAssociatedProductNumber;
  }

  get newProductNumberSearch(): boolean {
    return (
      !!Number(this.productNumberSearchInput) &&
      Number(this.quickOrderItem?.product?.productNumber) !==
        Number(this.productNumberSearchInput)
    );
  }

  get newCustomerProductNumberSearch(): boolean {
    return !!this.cpnInput && this.cpnInput !== this.currentQoeCpn;
  }

  get showQuantityInputs(): boolean {
    return (
      this.quickOrderItem?.product?.orderable &&
      this.quickOrderItem?.product?.summary?.cashCarryIndicator !== '2' &&
      this.quickOrderItem?.product?.summary?.cashCarryIndicator !== '3' &&
      !this.quickOrderItem?.product?.notFound &&
      !this.quickOrderItem?.product?.summary?.properties?.has(
        ProductPropertiesEnum.specialOrder,
      )
    );
  }

  get isProductSearch(): boolean {
    return /\d/.test(String(this.productNumberSearchInput));
  }

  get cpnInput(): string {
    if (!!this.customerProductNumberSearchInput) {
      const cpn = this.customerProductNumberSearchInput.trim();

      const initialValue = cpn;
      let sanitizedCpn = initialValue.replace(/[^a-zA-Z0-9\s\-_]/g, '');

      if (initialValue !== sanitizedCpn) {
        sanitizedCpn = initialValue.replace(/[^a-zA-Z0-9\s\-_]/g, '');
      }
      return sanitizedCpn;
    } else {
      return '';
    }
  }
}
